import { useGetAllAdminKPIPlanQuery } from "@/app/features/employee/performance/performance.api";
import useAuth from "@/hooks/useAuth";
import {
  ChartUpIcon,
  EditIcon,
  EyeIcon,
  Parabola04Icon,
  PencilEdit02Icon,
} from "@/icons";
import { formatDate, normalizeColumnValue } from "@/utils";
import { Button, Space, type TableProps } from "antd";
import dayjs from "dayjs";
import CustomTable from "../../../../components/CustomTable";
import {
  DISCLOSER_KEYS,
  FILTER_KEYS,
} from "../../../../constants/keys.constants";
import { useAppDiscloser, useAppFilters } from "../../../../hooks";
import { countParPageSerialNumber } from "../../../../utils/global.utils";
import ManageAppraisal from "./_components/ManageAppraisal";
import ManageKpiSetUp from "./_components/ManageKpiSetUp";

export default function OpenKpiSetUp() {
  const { organizationId } = useAuth();
  const { page, limit, filters, handlePagination } = useAppFilters(
    FILTER_KEYS.PERFORMANCE_OPEN_KPI,
  );

  const { data, isLoading, isFetching } = useGetAllAdminKPIPlanQuery({
    page,
    limit,
    organization_id: organizationId,
    ...filters,
  });

  const listData = data?.data?.plans || [];
  const total = data?.data?.pagination?.total || 0;

  const [config, handleOpen, handleClose] = useAppDiscloser<
    | "VIEW"
    | "EDIT"
    | "ADD"
    | "REOPEN"
    | "OPEN_APPRAISAL"
    | "UPDATE_APPRAISAL"
    | "VIEW_APPRAISAL"
  >(DISCLOSER_KEYS.PERFORMANCE_OPEN_KPI);

  const columns: TableProps<any>["columns"] = [
    {
      title: "SL NO.",
      align: "center",
      width: 80,
      render: (_, __, index) => countParPageSerialNumber(page, limit, index),
    },
    {
      title: "Name",
      dataIndex: "planName",
      render: (val: string) => normalizeColumnValue(val, "N/A", false),
    },

    {
      title: "Plan Period",
      dataIndex: "planPeriod",
      align: "center",
      render: (val: string) => normalizeColumnValue(val),
    },

    {
      title: "Plan Type",
      dataIndex: "planType",
      align: "center",
      render: (val: string) => normalizeColumnValue(val),
    },
    {
      title: "Created At",
      dataIndex: "createdAt",
      align: "center",
      render: (val: string) => formatDate(val),
    },
    {
      title: "Actions",
      dataIndex: "id",
      align: "end",
      render: (_, record) => (
        <Space size={8}>
          <Button
            size="small"
            className="action-btn"
            icon={<EyeIcon size={20} />}
            onClick={() => handleOpen({ type: "VIEW", data: record })}
          />
          <Button
            size="small"
            className="action-btn"
            icon={<PencilEdit02Icon size={20} />}
            onClick={() => handleOpen({ type: "EDIT", data: record })}
          />

          <Button
            size="small"
            className="btn-primary h-8"
            onClick={() => handleOpen({ type: "REOPEN", data: record })}
            disabled={
              !record?.endDate ||
              !dayjs(record.endDate).isSame(dayjs().subtract(1, "day"), "day")
            }
          >
            <Parabola04Icon className="text-neutral-700 text-base" /> Re-Open
            KPI
          </Button>

          {record?.appraisal_status === "NOT_STARTED" ? (
            <Button
              size="small"
              type="default"
              className="btn-primary-bordered h-8"
              onClick={() =>
                handleOpen({ type: "OPEN_APPRAISAL", data: record })
              }
            >
              <ChartUpIcon className="text-theme-primary size-4" /> Open
              Appraisal
            </Button>
          ) : (
            <Button
              size="small"
              type="default"
              className="btn-primary-bordered h-8"
              onClick={() =>
                handleOpen({ type: "UPDATE_APPRAISAL", data: record })
              }
            >
              <EditIcon className="text-theme-primary size-4" /> Update
              Appraisal
            </Button>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div>
      <CustomTable
        page={page}
        limit={limit}
        hideRowSelect
        columns={columns}
        dataSource={listData}
        onPaginate={handlePagination}
        customTitle="KPI Set-Up List"
        loading={isLoading || isFetching}
        total={total}
        filterSection={<></>}
      />
      <ManageKpiSetUp
        open={
          config?.open &&
          (config?.type === "VIEW" ||
            config?.type === "EDIT" ||
            config?.type === "ADD" ||
            config?.type === "REOPEN")
        }
        handleClose={handleClose}
        id={config?.data?.id}
        type={config?.type as "VIEW" | "EDIT" | "ADD" | "REOPEN"}
      />
      <ManageAppraisal
        open={
          config?.open &&
          (config?.type === "OPEN_APPRAISAL" ||
            config?.type === "UPDATE_APPRAISAL" ||
            config?.type === "VIEW_APPRAISAL")
        }
        handleClose={handleClose}
        id={config?.data?.id}
        type={
          config?.type === "VIEW_APPRAISAL"
            ? "VIEW"
            : config?.type === "UPDATE_APPRAISAL"
              ? "UPDATE"
              : "ADD"
        }
      />
    </div>
  );
}
